import {
  ICreatePortfolioOptimizationTemplateResponse,
  IOptimizationPayload,
  IPortfolioOptimizationTemplate,
  IPortfolioOptimizationTemplateDetails,
  IRunPortfolioOptimizationTemplateRequestBody,
  IRunPortfolioOptimizationTemplateResponse,
  IUpdatePortfolioOptimizationTemplateResponse,
} from '@/interfaces/portfolioOptimization.interface'
import axiosInstance from '@/utils/interceptor'

/**
 * Get Portfolio Optimization Templates List
 * @returns Promise<IPortfolioOptimizationTemplate[]> list of portfolio optimization templates
 */
export const getPortfolioOptimizationTemplates = async (): Promise<
  IPortfolioOptimizationTemplate[]
> => {
  const response = await axiosInstance.get(`optimizationTemplates`)
  return response.data
}

/**
 * Get Portfolio Optimization Template Details
 * @param id template id
 * @returns Promise<IPortfolioOptimizationTemplateDetails> template details
 */
export const getPortfolioOptimizationTemplateDetails = async (
  id: string,
): Promise<IPortfolioOptimizationTemplateDetails> => {
  const response = await axiosInstance.get(`optimizationTemplates/${id}`)
  return response.data as IPortfolioOptimizationTemplateDetails
}

/**
 * Create Portfolio Optimization Template
 * @param requestBody template details
 * @returns Promise<ICreatePortfolioOptimizationTemplateResponse> response of creating template
 */
export const createPortfolioOptimizationTemplate = async (
  requestBody: IOptimizationPayload,
): Promise<ICreatePortfolioOptimizationTemplateResponse> => {
  const response = await axiosInstance.post(
    `optimizationTemplates`,
    requestBody,
  )
  return response.data as ICreatePortfolioOptimizationTemplateResponse
}

/**
 * Update Portfolio Optimization Template
 * @param id template id
 * @param requestBody template details
 * @returns Promise<IUpdatePortfolioOptimizationTemplateResponse> response of updating template
 */
export const updatePortfolioOptimizationTemplate = async (
  id: string,
  requestBody: IOptimizationPayload,
): Promise<IUpdatePortfolioOptimizationTemplateResponse> => {
  const response = await axiosInstance.put(
    `optimizationTemplates/${id}`,
    requestBody,
  )
  return response.data as IUpdatePortfolioOptimizationTemplateResponse
}

/**
 * Delete Portfolio Optimization Template
 * @param id template id
 * @returns Promise<void> response of deleting template
 */
export const deletePortfolioOptimizationTemplate = async (
  id: string,
): Promise<{ success: boolean }> => {
  const response = await axiosInstance.delete(`optimizationTemplates/${id}`)
  return response.data as { success: boolean }
}

/**
 * Run Portfolio Optimization Template
 * @param id template id
 * @param requestBody template details
 * @returns Promise<IRunPortfolioOptimizationTemplateResponse> response of running template
 */
export const runPortfolioOptimizationTemplate = async (
  requestBody: IRunPortfolioOptimizationTemplateRequestBody,
): Promise<IRunPortfolioOptimizationTemplateResponse> => {
  const response = await axiosInstance.post(
    `optimizationTemplates/run-optimization`,
    requestBody,
  )
  return response.data as IRunPortfolioOptimizationTemplateResponse
}
